import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Plus, Wand2, Trash2, ArrowRight, LogOut, MapPin, Clock, Crown, Paperclip, X, FileText, File, AlertTriangle, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import useUiStore from '@/store/uiStore';
import Logo from '@/components/core/Logo';
import Onboarding from '@/components/Onboarding';

// Dashboard onboarding steps
const DASHBOARD_ONBOARDING_STEPS = [
  {
    target: '[data-testid="credits-display"]',
    title: 'Your Credits',
    description: 'Credits are used for AI features like explanations, quizzes, and Q&A. FREE plan starts with 10 credits for these features.',
    position: 'bottom'
  },
  {
    target: '[data-testid="generated-maps-display"]',
    title: 'AI Generated Maps',
    description: 'This shows how many AI-generated mind maps you\'ve created. FREE plan allows 1 AI-generated map.',
    position: 'bottom'
  },
  {
    target: '[data-testid="prompt-section"]',
    title: 'Generate Mind Maps',
    description: 'Enter any topic and our AI will create a comprehensive mind map for you. You can also upload reference documents to customize the content.',
    position: 'top'
  },
  {
    target: '[data-testid="maps-section"]',
    title: 'Your Mind Maps',
    description: 'All your mind maps appear here. Click on any map to open and edit it, or create a blank canvas to start from scratch.',
    position: 'top'
  }
];

// PRO user onboarding (without AI Maps counter step)
const DASHBOARD_ONBOARDING_STEPS_PRO = [
  {
    target: '[data-testid="credits-display"]',
    title: 'Your Credits',
    description: 'Credits are used for creating maps and AI features. Each map creation uses 1 credit. PRO plan gives you 50 credits!',
    position: 'bottom'
  },
  {
    target: '[data-testid="prompt-section"]',
    title: 'Generate Mind Maps',
    description: 'Enter any topic and our AI will create a comprehensive mind map for you. You can also upload reference documents to customize the content.',
    position: 'top'
  },
  {
    target: '[data-testid="maps-section"]',
    title: 'Your Mind Maps',
    description: 'All your mind maps appear here. With PRO, you have unlimited maps! Click any map to open it, or create a blank canvas.',
    position: 'top'
  }
];

export default function DashboardPage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { openUpgradeModal } = useUiStore();
  const [maps, setMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prompt, setPrompt] = useState('');
  const [persona, setPersona] = useState('student');
  const [generating, setGenerating] = useState(false);
  const [referenceFile, setReferenceFile] = useState(null);
  const [referenceText, setReferenceText] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => { loadMaps(); }, []);
  
  // Show onboarding after maps are loaded
  useEffect(() => {
    if (!loading && user) {
      const timer = setTimeout(() => setShowOnboarding(true), 500);
      return () => clearTimeout(timer);
    }
  }, [loading, user]);

  const loadMaps = async () => {
    try {
      const res = await api.get('/maps');
      setMaps(res.data);
    } catch { toast.error('Failed to load maps'); }
    finally { setLoading(false); }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) { toast.error('Enter a topic'); return; }
    setGenerating(true);
    try {
      const res = await api.post('/ai/generate', { 
        prompt, 
        persona,
        reference: referenceText || null
      });
      // Refresh user to update credits/generated_maps in UI
      await refreshUser();
      sessionStorage.setItem('atlas_generated_map', JSON.stringify(res.data));
      navigate('/canvas?generated=true');
    } catch (err) {
      if (err.response?.status === 403) {
        const detail = err.response?.data?.detail;
        if (detail === 'GENERATE_LIMIT_REACHED') {
          toast.error('Free plan allows 1 AI-generated map. Upgrade to PRO for unlimited.');
        } else if (detail === 'MAP_LIMIT_REACHED') {
          toast.error('Free plan allows max 3 maps. Upgrade to PRO for unlimited maps.');
        } else if (detail === 'NO_CREDITS') {
          toast.error('No credits remaining. Upgrade your plan for more credits.');
        } else {
          toast.error('Limit reached. Upgrade your plan.');
        }
        openUpgradeModal();
      } else toast.error('Generation failed');
    } finally { setGenerating(false); }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validTypes = ['application/pdf', 'text/plain'];
    const validExtensions = ['.pdf', '.txt'];
    const hasValidExt = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    
    if (!validTypes.includes(file.type) && !hasValidExt) {
      toast.error('Only PDF and TXT files are supported');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload/reference', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setReferenceFile({ name: file.name, chars: res.data.chars });
      setReferenceText(res.data.text);
      toast.success(`Reference loaded: ${res.data.chars.toLocaleString()} characters`);
    } catch (err) {
      toast.error('Failed to process file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveReference = () => {
    setReferenceFile(null);
    setReferenceText('');
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await api.post('/subscription/cancel');
      await refreshUser();
      setCancelModalOpen(false);
      toast.success('Subscription cancelled. You have been downgraded to the Free plan.');
    } catch (err) {
      toast.error('Failed to cancel subscription. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const handleDelete = async (mapId) => {
    try {
      await api.delete(`/maps/${mapId}`);
      setMaps(m => m.filter(x => x.id !== mapId));
      toast.success('Map deleted');
    } catch { toast.error('Delete failed'); }
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-sm" data-testid="dashboard-navbar">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Logo data-testid="dashboard-logo" />
          <div className="flex items-center gap-3">
            {/* Credits display for all users */}
            <div className="flex items-center gap-2 px-3 py-1 border border-border rounded-md bg-card" data-testid="credits-display">
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Credits</span>
              <span className="font-mono text-sm font-medium text-cobalt">{user?.credits ?? 0}</span>
            </div>
            
            {/* AI Maps counter for FREE users only */}
            {user?.plan === 'FREE' && (
              <div className="flex items-center gap-2 px-3 py-1 border border-border rounded-md bg-card" data-testid="generated-maps-display">
                <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">AI Maps</span>
                <span className="font-mono text-sm font-medium text-cobalt">{user?.generated_maps ?? 0}/1</span>
              </div>
            )}
            
            {user?.plan === 'FREE' ? (
              <Button size="sm" onClick={openUpgradeModal} data-testid="upgrade-btn"
                className="bg-orange text-white hover:bg-orange/90 font-body text-xs btn-press">
                <Crown className="w-3 h-3 mr-1" strokeWidth={1.5} /> Upgrade
              </Button>
            ) : (
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-cobalt border border-cobalt/20 px-2 py-1 rounded" data-testid="pro-badge">{user?.plan}</span>
            )}
            <Button variant="ghost" size="sm" onClick={logout} data-testid="dashboard-logout-btn" className="font-body text-sm">
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="mb-10 animate-fade-in-up">
          <h1 className="font-heading text-3xl font-[500] tracking-tight mb-1" data-testid="dashboard-heading">
            Welcome, {user?.name?.split(' ')[0] || 'Explorer'}
          </h1>
          <p className="font-body text-sm text-muted-foreground">Your knowledge maps and AI explorations.</p>
        </div>

        {/* Composer */}
        <div className="mb-10 max-w-2xl mx-auto border border-border bg-card rounded-md overflow-hidden animate-fade-in-up" style={{ animationDelay: '100ms' }}
          data-testid="prompt-section">
          <div className="flex items-center gap-3 px-5 py-3 border-b border-border bg-muted/30">
            <Select value={persona} onValueChange={setPersona}>
              <SelectTrigger className="w-[140px] h-8 text-sm border-border bg-card" data-testid="dashboard-persona-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="student">Student</SelectItem>
                <SelectItem value="researcher">Researcher</SelectItem>
                <SelectItem value="professional">Professional</SelectItem>
              </SelectContent>
            </Select>
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Persona</span>
          </div>
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder="What would you like to explore?"
            className="w-full px-5 py-4 text-base font-body bg-transparent border-0 outline-none resize-none placeholder:text-muted-foreground/50"
            rows={2}
            data-testid="dashboard-prompt-input"
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleGenerate(); } }}
          />
          
          {/* Reference File Display */}
          {referenceFile && (
            <div className="mx-5 mb-3 flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-md border border-border">
              {referenceFile.name.toLowerCase().endsWith('.pdf') ? (
                <FileText className="w-4 h-4 text-red-500 shrink-0" strokeWidth={1.5} />
              ) : (
                <File className="w-4 h-4 text-blue-500 shrink-0" strokeWidth={1.5} />
              )}
              <span className="text-xs font-body truncate flex-1">{referenceFile.name}</span>
              <span className="text-[10px] font-mono text-muted-foreground shrink-0">
                {referenceFile.chars.toLocaleString()} chars
              </span>
              <button 
                onClick={handleRemoveReference}
                className="p-0.5 hover:bg-muted rounded transition-colors shrink-0"
                data-testid="remove-reference-btn"
              >
                <X className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" strokeWidth={1.5} />
              </button>
            </div>
          )}
          
          <div className="flex items-center justify-between px-5 py-3 border-t border-border bg-muted/20">
            {/* File Upload Button */}
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.txt,application/pdf,text/plain"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-body text-muted-foreground hover:text-foreground hover:bg-muted rounded-md border border-border transition-colors"
                data-testid="upload-reference-btn"
                title="Upload PDF or TXT as reference"
              >
                {uploading ? (
                  <span className="spinner !w-3 !h-3" />
                ) : (
                  <Paperclip className="w-3.5 h-3.5" strokeWidth={1.5} />
                )}
                <span>{uploading ? 'Uploading...' : 'Add Reference'}</span>
              </button>
              <span className="text-[10px] font-mono text-muted-foreground/60">PDF, TXT</span>
            </div>
            
            <Button onClick={handleGenerate} disabled={generating || !prompt.trim()}
              data-testid="dashboard-generate-btn"
              className="bg-cobalt text-white hover:bg-cobalt/90 font-body text-sm btn-press h-9 px-5">
              {generating ? (
                <span className="flex items-center gap-2"><span className="spinner !w-4 !h-4 !border-white/30 !border-t-white" /> Generating...</span>
              ) : (
                <span className="flex items-center gap-1"><Wand2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Generate</span>
              )}
            </Button>
          </div>
        </div>

        {/* Maps Section */}
        <div data-testid="maps-section">
          {/* Maps Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Your Maps</span>
              <span className="font-mono text-[10px] text-muted-foreground/60">
                {user?.plan === 'FREE' ? `${maps.length}/3` : maps.length}
              </span>
            </div>
            {/* Disable blank canvas based on plan-specific limits */}
            {(() => {
              const isFree = user?.plan === 'FREE';
              const noCredits = !isFree && (user?.credits ?? 0) <= 0;  // Only check credits for PRO/MAX
              const mapLimitReached = isFree && maps.length >= 3;  // Only FREE has map limit
              const disabled = noCredits || mapLimitReached;
              const tooltipText = noCredits ? 'No credits remaining' : mapLimitReached ? 'Map limit reached (3/3)' : '';
              
              return (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => disabled ? (noCredits ? toast.error('No credits remaining. Upgrade for more credits.') : toast.error('Map limit reached. Upgrade to PRO for unlimited maps.')) : navigate('/canvas')} 
                  data-testid="new-blank-canvas-btn"
                  className={`font-body text-xs border-border hover:border-foreground/30 h-7 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={tooltipText}
                >
                  <Plus className="w-3 h-3 mr-1" strokeWidth={1.5} /> Blank Canvas
                </Button>
              );
            })()}
          </div>

          {/* Maps List */}
          {loading ? (
            <div className="flex justify-center py-20"><div className="spinner" /></div>
          ) : maps.length === 0 ? (
            <div className="border border-dashed border-border rounded-md py-16 text-center animate-fade-in" data-testid="empty-maps">
            <MapPin className="w-8 h-8 mx-auto text-muted-foreground/40 mb-3" strokeWidth={1} />
            <p className="font-body text-sm text-muted-foreground mb-1">No maps yet</p>
            <p className="font-body text-xs text-muted-foreground/60">Generate your first map above or start with a blank canvas.</p>
          </div>
        ) : (
          <div className="space-y-2 stagger-children" data-testid="maps-list">
            {maps.map(map => (
              <div
                key={map.id}
                className="flex items-center justify-between px-5 py-4 border border-border bg-card rounded-md card-hover cursor-pointer group"
                onClick={() => navigate(`/canvas?mapId=${map.id}`)}
                data-testid={`map-card-${map.id}`}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-body text-sm font-medium truncate">{map.title}</h3>
                    {map.description && <p className="font-body text-xs text-muted-foreground truncate">{map.description}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-mono text-[10px] text-muted-foreground/60 flex items-center gap-1">
                    <Clock className="w-3 h-3" strokeWidth={1.5} />
                    {formatDate(map.updated_at || map.created_at)}
                  </span>
                  <button
                    onClick={e => { e.stopPropagation(); handleDelete(map.id); }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:text-destructive"
                    data-testid={`delete-map-${map.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        </div> {/* End of Maps Section */}

        {/* Footer with Cancel Subscription for PRO and MAX users */}
        {(user?.plan === 'PRO' || user?.plan === 'MAX') && (
          <div className="mt-12 pt-6 border-t border-border">
            <div className="flex justify-end">
              <button
                onClick={() => setCancelModalOpen(true)}
                className="text-xs text-muted-foreground hover:text-destructive transition-colors underline underline-offset-2"
                data-testid="cancel-subscription-btn"
              >
                Cancel Subscription
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Cancel Subscription Modal */}
      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <DialogTitle className="text-center font-heading text-xl">
              Cancel Your Subscription?
            </DialogTitle>
            <DialogDescription className="text-center">
              Are you sure you want to cancel your {user?.plan} subscription?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800 font-medium mb-2">If you cancel, you will lose:</p>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Unlimited Mind Maps</li>
                <li>• Unlimited AI Q&A & Explanations</li>
                <li>• Export to PNG & PDF</li>
                <li>• Priority Support</li>
              </ul>
            </div>

            <p className="text-sm text-muted-foreground text-center">
              You will be immediately downgraded to the <span className="font-medium text-foreground">Free plan</span>.
            </p>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setCancelModalOpen(false)}
                className="flex-1"
                disabled={cancelling}
              >
                Keep {user?.plan}
              </Button>
              <Button
                onClick={handleCancelSubscription}
                disabled={cancelling}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-cancel-btn"
              >
                {cancelling ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Yes, Cancel'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Onboarding for first-time users */}
      {showOnboarding && user && (
        <Onboarding
          steps={user?.plan === 'FREE' ? DASHBOARD_ONBOARDING_STEPS : DASHBOARD_ONBOARDING_STEPS_PRO}
          onboardingKey="dashboard"
          hasCompleted={user?.onboarding?.dashboard}
          onComplete={() => setShowOnboarding(false)}
        />
      )}
    </div>
  );
}
